import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useTranslation } from 'react-i18next';

// Components
import Pagination from "./components/Pagination";
import Header from "./components/Header";
import ProgressBar from "./components/ProgressBar";
import Customers from "./components/Customers";
import Spinner from "components/spinner";

// Types
import { IAccordionStates } from "./types";

function DuplicateCustomers() {
  const { t } = useTranslation();
  const tBase = "views.duplicates.customers.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false); // used for scan / rescan
  const [initialLoad, setInitialLoad] = useState(true); // used for scan / rescan
  const [customerData, setCustomerData] = useState([] as any[]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]); // For backward navigation
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const savedItemsPerPage = localStorage.getItem("itemsPerPage");
    return savedItemsPerPage ? JSON.parse(savedItemsPerPage) : 20;
  });
  const [paginationInfo, setPaginationInfo] = useState({
    hasMore: false,
    hasPrev: false,
    totalCount: 0,
    firstCursor: null as string | null,
    lastCursor: null as string | null,
  });
  const [accordionStates, setAccordionStates] = useState<IAccordionStates>({});
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [showSpinnerMessage, setShowSpinnerMessage] = useState(false);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [clickedArea, setClickedArea] = useState(""); // used to determine which area was clicked (scan or rescan)
  const [scanComplete, setScanComplete] = useState(false); // used to determine if scan is complete
  const [yesScanned, setYesScanned] = useState(false); // used to determine if scan has been done before
  const [duplicatesExist, setDuplicatesExist] = useState(false);
  const [lastScanned, setLastScanned] = useState<Date | null>(null);
  const initialDataLoadDone = useRef(false);

  // Ensure immediate state changes are reflected before the browser paints
  useLayoutEffect(() => {
    setLoading(true);
    checkIfScanned();
    checkIfExists();
    setLoading(false);
  }, []);

  // Accordion
  const toggleAccordion = (id: string) => {
    setAccordionStates(prevState => ({
      ...prevState,
      [id]: !prevState[id]
    }));
  };

  const updateItemsPerPage = (newItemsPerPage: number) => {
    localStorage.setItem("itemsPerPage", JSON.stringify(newItemsPerPage));
    setItemsPerPage(newItemsPerPage);
    // Reset to first page when changing items per page
    setCursor(null);
    setCursorHistory([]);
    getCustomerData('next', null);
  };

  const getLastScanned = async () => {
    const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/duplicates/customers/lastScanned`);
    if (!res.data) return;
    const date = new Date(res.data);
    setLastScanned(date);
  };

  // Used for scan / re-scan button (REAL TIME) SSE
  const findDuplicates = ( whereClicked: string ) => {
    setScanComplete(false);
    setProgress(0);
    setProgressMessage("");
    if (whereClicked === "rescan") {
      setClickedArea("rescan");
    } else if (whereClicked === "scan") {
      setClickedArea("scan"); 
    }
    setLoading2(true); 
 
    if (eventSource) {

      eventSource.close();
      setEventSource(null);  // Immediately nullify the old EventSource reference  
    }

    const source = new EventSource(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/duplicates/customers/findDuplicates`,
      { withCredentials: true }
    );
    setEventSource(source);

    source.onmessage = (event) => {
      const { progress, message, type, finished } = JSON.parse(event.data);
      if (type === "progress1") {
        setProgress(progress * 0.05);
      } else if (type === "progress2") {
        setProgress(5 + progress * 0.95);
        setProgressMessage(message);
      }

      if (finished) { 

        source.close();
        eventSource?.close();

        setLoading2(false);
        setProgress(0);
        setProgressMessage("");
        setClickedArea("");
        setScanComplete(true); // follow the logic to the useEffect next
      }
    };

    source.onerror = error => {
      console.error("EventSource failed:", error);
      source.close();
      eventSource?.close();
      setLoading2(false);
      setProgress(0);
      setProgressMessage("");
      setScanComplete(true);
    };
    
  };

  // Gets customer data using cursor-based pagination
  const getCustomerData = useCallback(async (direction: 'next' | 'prev' = 'next', newCursor: string | null = null) => {
    setLoading(true);
    try {
      const currentCursor = newCursor !== null ? newCursor : cursor;
      const params: any = {
        limit: itemsPerPage,
        direction: direction
      };
      if (currentCursor) {
        params.cursor = currentCursor;
      }
      
      const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/duplicates/customers/getCustomerDataPaginated`, {
        params
      });
      
      setCustomerData(res.data.customers);
      setPaginationInfo({
        hasMore: res.data.hasMore,
        hasPrev: res.data.hasPrev,
        totalCount: res.data.totalCount,
        firstCursor: res.data.firstCursor,
        lastCursor: res.data.lastCursor,
      });
      setDuplicateCount(res.data.totalCount);
      
      // Update cursor after navigation
      // Note: "next" = decrement (lower IDs), "prev" = increment (higher IDs)
      if (res.data.customers.length > 0) {
        if (direction === 'next') {
          // Moving to "next" (decrement): store current firstCursor in history for going back
          // Only store if we're not on the first page (cursor is not null)
          if (res.data.firstCursor && currentCursor !== null) {
            setCursorHistory(prev => [...prev, res.data.firstCursor]);
          }
          // Update cursor to lastCursor (lowest ID on page) for next decrement navigation
          setCursor(res.data.lastCursor);
        } else {
          // Moving to "prev" (increment): the API returned records with higher IDs
          // Remove last entry from history since we've navigated back to that page
          const newHistory = [...cursorHistory];
          if (newHistory.length > 0) {
            newHistory.pop();
            setCursorHistory(newHistory);
          }
          // Update cursor: if we still have history, use lastCursor
          // Otherwise, we're at the top (highest IDs), so cursor should be null
          if (newHistory.length === 0) {
            setCursor(null); // We're at the top
          } else {
            // Set cursor to lastCursor of the page we're now on
            setCursor(res.data.lastCursor || null);
          }
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching customer data:", error);
      setLoading(false);
    }
  }, [cursor, cursorHistory, itemsPerPage]);

  const checkIfScanned = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/duplicates/customers/HasBeenScanned`);
      setYesScanned(res.data);
    } catch (error) {
      console.error("Error checking if scanned: ", error);
    }
  };

  const checkIfExists = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/duplicates/customers/CheckIfDuplicatesExist`);
      setDuplicatesExist(res.data);
    } catch (error) {
      console.error("Error checking if scanned: ", error);
    }
  };
  
  // Load initial data once when duplicates are ready (avoid re-running when getCustomerData identity changes)
  useEffect(() => {
    if (yesScanned && duplicatesExist && !initialDataLoadDone.current) {
      initialDataLoadDone.current = true;
      getCustomerData('next', null);
    }
  }, [yesScanned, duplicatesExist, getCustomerData]);

  // Navigate to next page (decrement - lower IDs)
  const paginateNext = () => {
    if (paginationInfo.hasMore) {
      // Use current cursor (lastCursor = lowest ID on page) or null for first page
      getCustomerData('next', cursor);
    }
  };

  // Navigate to previous page (increment - higher IDs)
  const paginatePrev = () => {
    if (paginationInfo.hasPrev) {
      // For increment navigation, use the lastCursor of the current page
      // This queries records with IDs > lastCursor (higher IDs)
      const prevCursor = paginationInfo.lastCursor;
      if (prevCursor) {
        getCustomerData('prev', prevCursor);
      } else {
        // No lastCursor means we're at top somehow, reset
        setCursor(null);
        setCursorHistory([]);
        getCustomerData('next', null);
      }
    }
  };

  // Calculate approximate page number for display
  // With cursor pagination, we can't know exact page, so we estimate based on total count
  const approximatePageNumber = cursorHistory.length + 1;
  const currentPage = approximatePageNumber || 1;
  const totalPages = Math.max(1, Math.ceil(paginationInfo.totalCount / itemsPerPage));

  useEffect(() => { 
    getLastScanned();
    checkIfExists();
    checkIfScanned();
    setProgress(0);
    setProgressMessage("");
    setClickedArea("");
    setInitialLoad(false)
  }, []);

  useEffect(() => { // after findDuplicates finishes
    if (scanComplete) {
      getLastScanned();
      checkIfExists();
      checkIfScanned();
      // Reset cursor and load first page
      setCursor(null);
      setCursorHistory([]);
      getCustomerData('next', null);
    }
  }, [scanComplete, getCustomerData]);

  useEffect(() => { // Scan in body, rescan in header
    if (clickedArea === "scan" || clickedArea === "rescan") {
      setShowSpinnerMessage(true);
    }
  }, [clickedArea]);

  return (
    <div className="p-4">

      {/* Header */}
      <Header {...{
        duplicateCount,
        accordionStates,
        setAccordionStates,
        customerData,
        findDuplicates,
        loading2,
        itemsPerPage,
        setItemsPerPage,
        currentPage: currentPage,
        updateItemsPerPage,
        scanComplete,
        yesScanned,
        lastScanned
      }} />

      {/* Header Row */}
      <div className="grid grid-cols-12 gap-4 text-[#000000B3] text-sm font-bold mt-6 mb-2">
        <div className="col-span-1 pl-2">% {tr("Match")}</div>
        <div className="col-span-2">{tr("Name")}</div>
        <div className="col-span-3">{tr("Email")}</div>
        <div className="col-span-3">{tr("Address")}</div>
        <div className="col-span-2">{tr("Phone")} #</div>
        <div className="col-span-1">{tr("Tools")}</div>
      </div>
      <div className="border-t border-primary "/>

      {/* Content */}
      {initialLoad || loading ? (
        <div className="text-center mt-8">
          <Spinner size={32} />
        </div>
      ) : (!yesScanned || clickedArea === "scan" || clickedArea === "rescan")  ? (
        <ProgressBar 
          {...{
            showSpinnerMessage,
            loading2,
            findDuplicates,
            progress,
            progressMessage,
            clickedArea,
            yesScanned
          }}
        />
      ) : yesScanned && duplicatesExist ? (
        <Customers 
        setCustomerData={setCustomerData} {...{
          customerData,
          toggleAccordion,
          accordionStates,
          setDuplicateCount,
          loading
        }}        
      />

      ) : (
        ""
      )}

      {/* Pagination */}
      {yesScanned && duplicatesExist && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          paginateNext={paginateNext}
          paginatePrev={paginatePrev}
          hasMore={paginationInfo.hasMore}
          hasPrev={paginationInfo.hasPrev}
        />
      )}

    </div>
  );
}

export default DuplicateCustomers;